import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {AsyncPipe, NgClass, NgIf, NgStyle} from "@angular/common";
import {ReactiveFormsModule} from "@angular/forms";
import {Store} from "@ngrx/store";
import * as UiActions from "../../../../state/ui/ui.actions";
import * as TagActions from "../../../../state/tag/tag.actions";
import {
  selectAllTags,
  selectAttachTagFailure,
  selectAttachTagSuccess,
  selectInvestmentColor,
  selectInvestmentLabel,
  selectInvestmentLoading,
  selectTagsError,
  selectTagsLoading
} from "../../../../state/tag/tag.selectors";
import {filter, Observable, Subscription, take} from "rxjs";
import {PopupService} from "../../../../services/popup.service";
import {ToggleButtonGroupComponent} from "../../../../common/form/toggle-button-group/toggle-button-group.component";
import {animate, style, transition, trigger} from "@angular/animations";

interface TagFilter {
  category: string;
  value: string;
  label: string;
}

interface SelectedFilters {
  [key: string]: string[];
}

@Component({
  selector: 'app-tags',
  standalone: true,
  imports: [NgIf, ReactiveFormsModule, ToggleButtonGroupComponent, NgClass, AsyncPipe, NgStyle],
  templateUrl: './tags.component.html',
  styleUrl: './tags.component.scss',
  animations: [
    trigger('fadeFromRight', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateX(20px)'
        }),
        animate('300ms ease-out', style({
          opacity: 1,
          transform: 'translateX(0)'
        }))
      ])
    ])
  ]
})
export class TagsComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @Input() aside$: Observable<boolean>;

  @Output() dataEmitter: EventEmitter<any> = new EventEmitter();
  @Output() handleAsideEmitter: EventEmitter<any> = new EventEmitter();
  @Input() report: any;
  @Output() selectedFiltersChange = new EventEmitter<TagFilter[]>();

  reportId: number;
  reportTags: any;

  // Investment data
  investmentLabel: string | null = null;
  investmentColor: string | null = null;
  investmentLoading: boolean = false;

  // Color mapping configuration - easy to modify in one place
  private colorMappings = {
    // API colors (keys) mapped to Tailwind colors (values)
    'ff0000': { // Red from API
      progress: '#ef4444', // red-500
      text: '#ef4444'      // red-500
    },
    'ffff00': { // Yellow/Orange from API
      progress: '#f59e0b', // amber-500
      text: '#f59e0b'      // amber-500
    },
    '66cc00': { // Green from API
      progress: '#22c55e', // green-500
      text: '#22c55e'      // green-500
    }
  };

// Default color to use if no mapping is found
  private defaultColorMapping = {
    progress: '#ef4444', // Default to red-500
    text: '#ef4444'
  };

  // Subscriptions
  tagsSubscription: Subscription;
  errorSubscription: Subscription;
  loadingSubscription: Subscription;
  investmentLabelSubscription: Subscription;
  investmentColorSubscription: Subscription;
  investmentLoadingSubscription: Subscription;

  // Tags loading state
  loading: boolean = false;

  // Categories that trigger investment API call
  investmentCategories = ['myJudgement', 'dealStages', 'dealValues'];

  // Notification tracking
  private loadingNotificationId: number | null = null;

  // Filter options and selected filters
  filterOptions = {
    roles: [],
    dealStages: [],
    dealValues: [],
    statuses: [],
    closeDates: [],
    myJudgement: [],
    icpName: []
  };

  selectedFilters: SelectedFilters = {
    roles: [],
    dealStages: [],
    dealValues: [],
    statuses: [],
    closeDates: [],
    myJudgement: [],
    icpName: []
  };

  private isInitialLoad = true;

  constructor(private store: Store, private popupService: PopupService) {}

  ngOnInit() {
    if (this.report) {
      this.reportId = this.report.id;
      this.reportTags = this.report.tagsData;

      // Populate investment data directly from a report if available
      if (this.report.investment_label && this.report.investment_color) {
        this.investmentLabel = this.report.investment_label;
        this.investmentColor = this.report.investment_color;
      } else {
        // Only fetch from API if not available in a report
        this.fetchInvestmentData();
      }
    }

    // Subscribe to investment data
    this.investmentLabelSubscription = this.store.select(selectInvestmentLabel)
      .subscribe(label => {
        if (label) this.investmentLabel = label;
      });

    this.investmentColorSubscription = this.store.select(selectInvestmentColor)
      .subscribe(color => {
        if (color) this.investmentColor = color;
      });

    // Subscribe to loading state and manage notifications
    this.investmentLoadingSubscription = this.store.select(selectInvestmentLoading)
      .subscribe(loading => {
        this.investmentLoading = loading;

        // Only remove notification if we have a notification ID
        // This ensures we don't try to remove notifications on initial load
        if (!loading && this.loadingNotificationId !== null) {
          this.popupService.removeNotification(this.loadingNotificationId);
          this.loadingNotificationId = null;
        }
      });
  }

  ngAfterViewInit(): void {
    // Wait a short time to ensure all values are loaded and rendered
    setTimeout(() => {
      this.isInitialLoad = false;
    }, 100);
  }

  // Make sure this method doesn't show a notification
  fetchInvestmentData(): void {
    // We're fetching data for initial load
    this.isInitialLoad = true;

    if (this.reportId) {
      this.store.dispatch(TagActions.loadInvestment({
        searchId: this.reportId
      }));
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['report']?.currentValue) {
      // This is a new report, consider it an initial load
      this.isInitialLoad = true;

      this.reportId = this.report.id;

      // Populate investment data directly from a report if available
      if (this.report.investment_label && this.report.investment_color) {
        this.investmentLabel = this.report.investment_label;
        this.investmentColor = this.report.investment_color;
      } else {
        // Only fetch from API if not available in a report
        this.fetchInvestmentData();
      }

      if (this.report.tagsData) {
        this.populateSelectedFilters(this.report.tagsData);
      }

      this.getTags();

      // After a short delay, allow transitions for subsequent changes
      setTimeout(() => {
        this.isInitialLoad = false;
      }, 200);
    }
  }

  ngOnDestroy() {
    // Clear investment data when component is destroyed
    this.store.dispatch(TagActions.resetInvestment());

    // Clean up subscriptions
    [
      this.tagsSubscription,
      this.errorSubscription,
      this.loadingSubscription,
      this.investmentLabelSubscription,
      this.investmentColorSubscription,
      this.investmentLoadingSubscription
    ].forEach(sub => sub?.unsubscribe());

    // Remove any lingering notifications
    if (this.loadingNotificationId !== null) {
      this.popupService.removeNotification(this.loadingNotificationId);
      this.loadingNotificationId = null;
    }
  }

  // Manage loading notification
  private manageLoadingNotification(loading: boolean): void {
    if (loading) {
      const message = 'Updating investment data...';
      // this.popupService.notify(message, 'Loading', 'info', 30000);

      // Store notification ID
      setTimeout(() => {
        this.popupService.notifications$.pipe(take(1)).subscribe(notifications => {
          const matchingNotification = notifications.find(n => n.message === message);
          if (matchingNotification) {
            this.loadingNotificationId = matchingNotification.id;
          }
        });
      }, 10);
    } else if (this.loadingNotificationId !== null) {
      this.popupService.removeNotification(this.loadingNotificationId);
      this.loadingNotificationId = null;
    }
  }

  onFilterChange(category: string, selectedValues: string | string[]): void {
    // Ensure the value is always an array
    const processedValues = Array.isArray(selectedValues)
      ? selectedValues
      : [selectedValues];

    // Update selected filters
    this.selectedFilters = {
      ...this.selectedFilters,
      [category]: processedValues
    };

    // Emit selected filters
    this.emitSelectedFilters();

    // Check if the category is one of the investment categories
    const investmentCategories = ['myJudgement', 'dealStages', 'dealValues'];
    const isInvestmentCategory = investmentCategories.includes(category);

    // If it's an investment category, show loading notification
    if (isInvestmentCategory) {
      const message = 'Updating investment data...';
      // this.popupService.notify(message, 'Loading', 'info', 30000);

      // Store notification ID for later removal
      setTimeout(() => {
        this.popupService.notifications$.pipe(take(1)).subscribe(notifications => {
          const matchingNotification = notifications.find(n => n.message === message);
          if (matchingNotification) {
            this.loadingNotificationId = matchingNotification.id;
          }
        });
      }, 10);
    }

    // Create and attach tags
    const selectedTags = this.createSelectedTagsObject();
    this.attachTags(selectedTags.tags);
  }

  attachTags(tags: any): void {
    this.loading = true;

    // Make sure transitions are enabled for changes
    this.isInitialLoad = false;

    const payload = {
      searchId: this.reportId,
      tags: tags,
    };

    // Dispatch the attachTag action
    this.store.dispatch(TagActions.attachTag(payload));

    // Handle success/failure
    this.store.select(selectAttachTagSuccess).pipe(
      filter(success => !!success),
      take(1)
    ).subscribe(() => {
      this.loading = false;

      // Check if any investment categories were changed
      const investmentCategories = ['myJudgement', 'dealStages', 'dealValues'];
      const hasInvestmentCategories = Object.keys(tags).some(category =>
        investmentCategories.includes(category) && tags[category]?.length > 0
      );

      // If any investment category was changed, fetch investment data
      if (hasInvestmentCategories) {
        // Dispatch loadInvestment action
        this.store.dispatch(TagActions.loadInvestment({
          searchId: this.reportId
        }));
      }

      // Always emit event to refresh the report
      this.dataEmitter.emit({
        type: 'refreshReport',
        searchId: this.reportId,
        source: 'tags-component'
      });
    });

    this.store.select(selectAttachTagFailure).pipe(
      filter(error => !!error),
      take(1)
    ).subscribe(error => {
      this.popupService.notify('Failed to attach tags. Please try again.', 'Error', 'error', 2500);
      this.loading = false;
    });
  }

  closeAside(): void {
    this.store.dispatch(UiActions.toggleAside({ isOpen: false }));
  }

  getTags(): void {
    this.store.dispatch(TagActions.loadTags());

    this.loadingSubscription = this.store.select(selectTagsLoading)
      .pipe(
        filter(loading => !loading),
        take(1)
      )
      .subscribe(() => {
        this.tagsSubscription = this.store.select(selectAllTags)
          .subscribe((tags: any) => {
            if (tags && typeof tags === 'object') {
              this.filterOptions = {
                roles: this.mapTagOptions(tags.roles || []),
                dealStages: this.mapTagOptions(tags.dealStages || []),
                dealValues: this.mapTagOptions(tags.dealValues || []),
                statuses: this.mapTagOptions(tags.statuses || []),
                closeDates: this.mapTagOptions(tags.closeDates || []),
                myJudgement: this.mapTagOptions(tags.myJudgement || []),
                icpName: this.mapTagOptions(tags.icpName || [])
              };
            }
          });
      });

    this.errorSubscription = this.store.select(selectTagsError)
      .pipe(filter(error => !!error), take(1))
      .subscribe(error => {
        console.error('Failed to load tags:', error);
      });
  }

  populateSelectedFilters(tagsData: any[]): void {
    // Reset all categories
    for (let category in this.selectedFilters) {
      this.selectedFilters[category] = Array.isArray(this.selectedFilters[category]) ? [] : null;
    }

    if (!tagsData || tagsData.length === 0) return;

    // Process tags
    tagsData.forEach(tag => {
      const category = this.toCamelCase(tag.type);
      const value = tag.slug;

      if (this.selectedFilters[category] !== undefined) {
        if (Array.isArray(this.selectedFilters[category])) {
          (this.selectedFilters[category] as string[]).push(value);
        } else {
          this.selectedFilters[category] = value;
        }
      }
    });

    this.emitSelectedFilters();
  }

  private emitSelectedFilters(): void {
    const transformedFilters: TagFilter[] = [];

    Object.entries(this.selectedFilters).forEach(([category, values]) => {
      const processedValues = Array.isArray(values) ? values : [values];

      processedValues.forEach(value => {
        if (value !== null && value !== undefined) {
          transformedFilters.push({
            category,
            value,
            label: this.getLabelForValue(category, value)
          });
        }
      });
    });

    this.selectedFiltersChange.emit(transformedFilters);
  }

  createSelectedTagsObject(): any {
    const tagsObject = {
      tags: {
        roles: [],
        dealStages: [],
        dealValues: [],
        statuses: [],
        closeDates: [],
        myJudgement: [],
        icpName: []
      }
    };

    for (const category in this.selectedFilters) {
      const values = this.selectedFilters[category];
      const processedValues = Array.isArray(values) ? values : [values];

      if (processedValues.length > 0) {
        tagsObject.tags[category] = processedValues;
      }
    }

    return tagsObject;
  }

  toCamelCase(str: string): string {
    return str
      .replace(/[-_\s]+(.)?/g, (match, chr) => chr ? chr.toUpperCase() : '')
      .replace(/^[A-Z]/, match => match.toLowerCase());
  }

  private getLabelForValue(category: string, value: string): string {
    const options = this.filterOptions[category];
    const option = options?.find(opt => opt.value === value);
    return option?.label || value;
  }

  mapTagOptions(tags: any[]): { label: string, value: string }[] {
    return tags.map(tag => ({
      label: tag.name?.en || '',
      value: tag.slug?.en || ''
    }));
  }

  isSelected(category: string, value: string): boolean {
    return this.selectedFilters[category].includes(value);
  }

  getColorFromPath(colorPath: string | null): string {
    if (!colorPath) return 'red-500';

    const colorMatch = colorPath.match(/#([A-Fa-f0-9]{6})/);
    if (colorMatch && colorMatch[1]) {
      const hexColor = colorMatch[1].toLowerCase();

      const colorMap = {
        'ff0000': 'red-500',
        'ffff00': 'orange-500',
        '66cc00': 'green-500'
      };

      return colorMap[hexColor] || 'red-500';
    }

    return 'red-500';
  }

  // Progress bar styling
  get progressBarStyle(): any {
    // Always return a style regardless of investmentColor
    return {
      'position': 'relative',
      'height': '4px',
      'background-color': 'rgb(222 214 255 / 50%)', // Light violet background
      'border-radius': '2px',
      'overflow': 'hidden',
      'margin-bottom': '12px',
      /*'box-shadow': 'inset 0 1px 2px rgba(0, 0, 0, 0.1)'*/ // inset shadow
    };
  }

  get progressBarFillStyle(): any {
    const displayColor = this.getDisplayColorFromInvestment('progress');
    const progressWidth = this.getProgressWidthFromColor();

    return {
      'position': 'absolute',
      'top': '0',
      'left': '0',
      'height': '100%',
      'width': progressWidth,
      'background-color': displayColor,
      'border-radius': '2px',
      // Only apply transition after initial load
      'transition': this.isInitialLoad ? 'none' : 'width 0.6s ease-in-out, background-color 0.6s ease-in-out'
    };
  }

  get investmentStyleHeading(): any {
    // Work even if investmentColor is null
    const textColor = this.getDisplayColorFromInvestment('text');

    return {
      'color': textColor
    };
  }

  private getDisplayColorFromInvestment(type: 'progress' | 'text'): string {
    if (!this.investmentColor) return this.defaultColorMapping[type];

    const colorMatch = this.investmentColor.match(/#([A-Fa-f0-9]{6})/);
    if (colorMatch && colorMatch[1]) {
      const hexColor = colorMatch[1].toLowerCase();

      // Look up the mapping or use default if not found
      const mapping = this.colorMappings[hexColor] || this.defaultColorMapping;
      return mapping[type];
    }

    return this.defaultColorMapping[type];
  }

  private getProgressWidthFromColor(): string {
    if (!this.investmentColor) return '33%';

    const colorMatch = this.investmentColor.match(/#([A-Fa-f0-9]{6})/);
    if (colorMatch && colorMatch[1]) {
      const hexColor = colorMatch[1].toLowerCase();

      // Check color and return appropriate width
      if (hexColor === '66cc00') {
        return '100%'; // Green = 100%
      } else if (hexColor === 'ffff00') {
        return '66.6%'; // Yellow/Orange = 66.6%
      } else {
        return '33%'; // Red or any other color = 33%
      }
    }

    return '33%'; // Default fallback
  }

  private getInvestmentHexColor(): string {
    if (!this.investmentColor) return '#ef4444';

    const colorMatch = this.investmentColor.match(/#([A-Fa-f0-9]{6})/);
    if (colorMatch) {
      return this.investmentColor;
    }

    return '#ef4444';
  }
}
